import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useDataFeedWizardStore,
  useDirtyTracking,
} from '../../../../store/dataFeedWizard';
import { UseLinkAccountsStepReturn } from './types';
import {
  CustodianItem,
  AccountMappingRequest,
} from '../../../../types/generatedTypes';
import Actions from '../../../../store/Actions';
import { useSyncOperations } from '../../../../hooks/useSyncOperations';

export const useLinkAccountsStep = (): UseLinkAccountsStepReturn => {
  const {
    linkAccountsData,
    setLinkAccountsData,
    updateAccountMapping,
    updateAccountUploadEnabled,
    custodianOptions,
    setCustodianOptions,
    setCurrentStepButtonState,
    registerStepActions,
    clearStepActions,
    sessionId,
    setStepLoading,
    setPristineState,
  } = useDataFeedWizardStore();

  const { isDirty } = useDirtyTracking();
  const [isSaving, setIsSaving] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const { syncPortfolioCustodianFromIllio } = useSyncOperations();

  // Load accounts and custodians on mount - skip if data already cached in store
  useEffect(() => {
    const loadData = async () => {
      // Skip fetch if data already exists in store (cached from previous visit)
      if (linkAccountsData.mappings.length > 0 && custodianOptions.length > 0) {
        // Set pristine to cached data
        setPristineState('step2', linkAccountsData.mappings);
        return;
      }

      if (!sessionId) {
        console.warn('Missing sessionId - cannot load accounts');
        return;
      }

      setStepLoading(true);
      try {
        // Load both accounts and custodians in parallel
        const [accountsResponse, custodiansResponse] = await Promise.all([
          Actions.extractWizardAccounts(sessionId),
          Actions.getWizardCustodians(),
        ]);

        // Set custodian options - Backend returns custodianId, custodianName, portfolioId, clientName, portfolioName, accountNumber, uploadTransactionsEnabled
        let custodians = custodiansResponse.data.custodians.map(
          (c: CustodianItem) => ({
            custodianId: c.custodianId,
            custodianName: c.custodianName,
            portfolioId: c.portfolioId,
            clientName: c.clientName,
            portfolioName: c.portfolioName,
            accountNumber: c.accountNumber,
            uploadTransactionsEnabled: c.uploadTransactionsEnabled,
          })
        );

        // If no custodians, sync from Illio and reload
        if (custodians.length === 0) {
          setIsSyncing(true);
          try {
            await syncPortfolioCustodianFromIllio(async () => {
              // Reload custodians after sync
              const reloadedResponse = await Actions.getWizardCustodians();
              custodians = reloadedResponse.data.custodians.map(
                (c: CustodianItem) => ({
                  custodianId: c.custodianId,
                  custodianName: c.custodianName,
                  portfolioId: c.portfolioId,
                  clientName: c.clientName,
                  portfolioName: c.portfolioName,
                  accountNumber: c.accountNumber,
                  uploadTransactionsEnabled: c.uploadTransactionsEnabled,
                })
              );
            });
          } catch (error) {
            console.error('Failed to sync portfolios from Illio:', error);
          } finally {
            setIsSyncing(false);
          }
        }

        setCustodianOptions(custodians);

        // Extract account ignore preferences from org settings (accountNumber -> boolean)
        const accountIgnorePreferences: Record<string, boolean> =
          custodiansResponse.data.accountIgnorePreferences;

        // Initialize mappings if not already set - pre-populate with existing account matches
        if (linkAccountsData.mappings.length === 0) {
          const initialMappings = accountsResponse.data.accountNumbers.map(
            accountNumber => {
              // Find custodian that already has this account number
              const matchingCustodian = custodians.find(
                c => c.accountNumber === accountNumber
              );

              // Priority for uploadTransactionsEnabled:
              // 1) If account has a matching custodian, use that custodian's uploadTransactionsEnabled
              // 2) If no custodian but has org preference, use accountIgnorePreferences[accountNumber]
              // 3) Default to true
              const uploadTransactionsEnabled = matchingCustodian
                ? matchingCustodian.uploadTransactionsEnabled
                : (accountIgnorePreferences[accountNumber] ?? true);

              return {
                accountNumber,
                custodianId: matchingCustodian?.custodianId || null,
                custodianName: matchingCustodian?.custodianName || null,
                portfolioId: matchingCustodian?.portfolioId || null,
                clientName: matchingCustodian?.clientName || null,
                portfolioName: matchingCustodian?.portfolioName || null,
                uploadTransactionsEnabled,
              };
            }
          );
          setLinkAccountsData({ mappings: initialMappings });
          // Set pristine to freshly loaded data
          setPristineState('step2', initialMappings);
        }
      } catch (error) {
        console.error('Failed to load accounts or custodians:', error);
        // Show error state - no fallbacks
        setCustodianOptions([]);
      } finally {
        setStepLoading(false);
      }
    };

    loadData();
    // Note: setLinkAccountsData, setCustodianOptions, setPristineState omitted from deps - Zustand store functions are stable
  }, [sessionId, linkAccountsData.mappings.length, custodianOptions.length]);

  // Track initial sort to preserve row position after selection
  const initialSortDone = useRef(false);
  const sortedIndices = useRef<Record<string, number>>({});

  // Transform mappings to DataGrid rows format
  const rows = useMemo(() => {
    const mappedRows = linkAccountsData.mappings.map(mapping => ({
      id: mapping.accountNumber,
      accountNumber: mapping.accountNumber,
      custodianId: mapping.custodianId,
      custodianName: mapping.custodianName,
      portfolioId: mapping.portfolioId,
      uploadTransactionsEnabled: mapping.uploadTransactionsEnabled !== false, // Default true
    }));

    // On first load: sort unmapped first, pre-mapped at bottom
    if (!initialSortDone.current && mappedRows.length > 0) {
      mappedRows.sort((a, b) => {
        const aIsMapped = a.custodianId !== null;
        const bIsMapped = b.custodianId !== null;
        if (!aIsMapped && bIsMapped) return -1;
        if (aIsMapped && !bIsMapped) return 1;
        return 0;
      });
      mappedRows.forEach((row, index) => {
        sortedIndices.current[row.id] = index;
      });
      initialSortDone.current = true;
      return mappedRows;
    }

    // After initial sort: preserve order using stored indices
    return mappedRows.sort((a, b) => {
      const indexA = sortedIndices.current[a.id] ?? Number.MAX_SAFE_INTEGER;
      const indexB = sortedIndices.current[b.id] ?? Number.MAX_SAFE_INTEGER;
      return indexA - indexB;
    });
  }, [linkAccountsData.mappings]);

  const getAvailableOptionsForRow = (rowId: string) => {
    // Get all selected portfolioId:custodianId combos except for the current row
    // A custodian can appear in multiple portfolios, so we need both IDs for unique identification
    const selectedCombos = linkAccountsData.mappings
      .filter(
        mapping =>
          mapping.accountNumber !== rowId && mapping.custodianId !== null
      )
      .map(mapping => `${mapping.portfolioId}:${mapping.custodianId}`);

    const currentRowMapping = linkAccountsData.mappings.find(
      mapping => mapping.accountNumber === rowId
    );
    const currentRowCombo = currentRowMapping?.custodianId
      ? `${currentRowMapping.portfolioId}:${currentRowMapping.custodianId}`
      : null;

    return custodianOptions.filter(option => {
      const optionCombo = `${option.portfolioId}:${option.custodianId}`;

      // Exclude if already mapped to a different account in backend
      if (option.accountNumber && option.accountNumber !== rowId) {
        return false;
      }

      // Exclude if selected by another row in current session (unless it's this row's selection)
      if (
        selectedCombos.includes(optionCombo) &&
        optionCombo !== currentRowCombo
      ) {
        return false;
      }

      return true;
    });
  };

  const handleCustodianChange = (
    rowId: string,
    custodianId: string | null,
    portfolioId: string | null
  ) => {
    const index = linkAccountsData.mappings.findIndex(
      m => m.accountNumber === rowId
    );
    if (index === -1) return;

    // Find the custodian by both portfolioId and custodianId for unique identification
    const selectedCustodian = custodianOptions.find(
      c => c.custodianId === custodianId && c.portfolioId === portfolioId
    );
    updateAccountMapping(
      index,
      custodianId,
      selectedCustodian?.custodianName || null,
      portfolioId,
      selectedCustodian?.clientName || null,
      selectedCustodian?.portfolioName || null
    );
  };

  const handleUploadToggle = (rowId: string, enabled: boolean) => {
    const index = linkAccountsData.mappings.findIndex(
      m => m.accountNumber === rowId
    );
    if (index === -1) return;
    updateAccountUploadEnabled(index, enabled);
  };

  // Memoize validateFields to prevent infinite loops in action registration useEffect
  // Account mapping is optional - always valid
  const validateFields = useCallback((): boolean => {
    return true;
  }, []);

  // Save account mappings to backend
  const handleNext = useCallback(async (): Promise<boolean | number> => {
    if (!sessionId) {
      console.error('Missing sessionId - cannot save account mappings');
      return false;
    }

    setIsSaving(true);
    try {
      // Backend expects AccountMappingRequest with mappings array containing accountNumber, custodianId, portfolioId, uploadTransactionsEnabled
      const requestData: AccountMappingRequest = {
        mappings: linkAccountsData.mappings.map(mapping => ({
          accountNumber: mapping.accountNumber,
          custodianId: mapping.custodianId,
          portfolioId: mapping.portfolioId,
          uploadTransactionsEnabled: mapping.uploadTransactionsEnabled ?? true,
        })),
      };

      await Actions.saveWizardAccountMappings(sessionId, requestData);

      // Update pristine state after successful save
      setPristineState('step2', linkAccountsData.mappings);

      return true;
    } catch (error) {
      console.error('Failed to save account mappings:', error);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [
    validateFields,
    sessionId,
    linkAccountsData.mappings,
    isDirty,
    setPristineState,
  ]);

  // Update store with button state - account mapping is optional
  // Note: setCurrentStepButtonState is omitted from deps - Zustand store functions are stable
  useEffect(() => {
    setCurrentStepButtonState({
      disabled: isSaving,
      loading: isSaving,
    });
  }, [isSaving]);

  // Register step actions when component mounts
  // Note: registerStepActions and clearStepActions are omitted from deps - Zustand store functions are stable
  useEffect(() => {
    registerStepActions({
      onNext: handleNext,
      validateFields,
    });

    return () => {
      clearStepActions();
    };
  }, [handleNext, validateFields]);

  return {
    rows,
    custodianOptions,
    getAvailableOptionsForRow,
    handleCustodianChange,
    handleUploadToggle,
    validateFields,
    isSaving,
    isSyncing,
  };
};
